import React, { useRef, useEffect, useState } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon, Code, Eye, Heading1, Heading2, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';

const RichTextEditor = ({ value, onChange }) => {
  const editorRef = useRef(null);
  const [isCodeView, setIsCodeView] = useState(false);
  const [htmlContent, setHtmlContent] = useState(value || '');

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
    setHtmlContent(value || '');
  }, [value]);

  const handleEditorInput = () => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML;
      setHtmlContent(html);
      if (onChange) onChange(html);
    }
  };

  const handleTextAreaChange = (e) => {
    const val = e.target.value;
    setHtmlContent(val);
    if (onChange) onChange(val);
  };

  const executeCommand = (command, val = null) => {
    document.execCommand(command, false, val);
    handleEditorInput();
  };

  const addLink = () => {
    const url = prompt("Link URL'sini girin:", "https://");
    if (url) {
      executeCommand("createLink", url);
    }
  };

  return (
    <div className="border border-slate-300 rounded-2xl overflow-hidden focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-500 transition-all bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-1 p-2 bg-slate-50 border-b border-slate-200">
        <button
          type="button"
          onClick={() => executeCommand('bold')}
          className="p-2 hover:bg-slate-200 rounded-lg text-slate-600 transition"
          title="Kalın"
        >
          <Bold size={16} />
        </button>
        <button
          type="button"
          onClick={() => executeCommand('italic')}
          className="p-2 hover:bg-slate-200 rounded-lg text-slate-600 transition"
          title="Eğik"
        >
          <Italic size={16} />
        </button>
        <button
          type="button"
          onClick={() => executeCommand('underline')}
          className="p-2 hover:bg-slate-200 rounded-lg text-slate-600 transition"
          title="Altı Çizili"
        >
          <Underline size={16} />
        </button>
        
        <div className="w-px h-6 bg-slate-300 mx-1" />

        <button
          type="button"
          onClick={() => executeCommand('formatBlock', '<h1>')}
          className="p-2 hover:bg-slate-200 rounded-lg text-slate-600 transition"
          title="Başlık 1"
        >
          <Heading1 size={16} />
        </button>
        <button
          type="button"
          onClick={() => executeCommand('formatBlock', '<h2>')}
          className="p-2 hover:bg-slate-200 rounded-lg text-slate-600 transition"
          title="Başlık 2"
        >
          <Heading2 size={16} />
        </button>

        <div className="w-px h-6 bg-slate-300 mx-1" />

        <button
          type="button"
          onClick={() => executeCommand('justifyLeft')}
          className="p-2 hover:bg-slate-200 rounded-lg text-slate-600 transition"
          title="Sola Hizala"
        >
          <AlignLeft size={16} />
        </button>
        <button
          type="button"
          onClick={() => executeCommand('justifyCenter')}
          className="p-2 hover:bg-slate-200 rounded-lg text-slate-600 transition"
          title="Ortala"
        >
          <AlignCenter size={16} />
        </button>
        <button
          type="button"
          onClick={() => executeCommand('justifyRight')}
          className="p-2 hover:bg-slate-200 rounded-lg text-slate-600 transition"
          title="Sağa Hizala"
        >
          <AlignRight size={16} />
        </button>

        <div className="w-px h-6 bg-slate-300 mx-1" />

        <button
          type="button"
          onClick={() => executeCommand('insertUnorderedList')}
          className="p-2 hover:bg-slate-200 rounded-lg text-slate-600 transition"
          title="Sırasız Liste"
        >
          <List size={16} />
        </button>
        <button
          type="button"
          onClick={() => executeCommand('insertOrderedList')}
          className="p-2 hover:bg-slate-200 rounded-lg text-slate-600 transition"
          title="Sıralı Liste"
        >
          <ListOrdered size={16} />
        </button>
        
        <button
          type="button"
          onClick={addLink}
          className="p-2 hover:bg-slate-200 rounded-lg text-slate-600 transition"
          title="Bağlantı Ekle"
        >
          <LinkIcon size={16} />
        </button>

        <div className="ml-auto flex items-center">
          <button
            type="button"
            onClick={() => setIsCodeView(!isCodeView)}
            className="p-2 hover:bg-slate-200 rounded-lg text-blue-600 font-semibold flex items-center gap-1.5 text-xs transition"
            title={isCodeView ? "Görsel Düzenleyiciye Geç" : "HTML Kod Görünümüne Geç"}
          >
            {isCodeView ? (
              <>
                <Eye size={14} /> Görsel
              </>
            ) : (
              <>
                <Code size={14} /> HTML
              </>
            )}
          </button>
        </div>
      </div>

      {/* Editor Content Area */}
      <div className="relative min-h-[200px]">
        <div
          ref={editorRef}
          contentEditable={!isCodeView}
          onInput={handleEditorInput}
          className={`w-full min-h-[200px] p-4 text-sm font-medium focus:outline-none overflow-y-auto prose prose-sm max-w-none ${isCodeView ? 'hidden' : 'block'}`}
          style={{ minHeight: '200px' }}
        />
        <textarea
          value={htmlContent}
          onChange={handleTextAreaChange}
          className={`w-full min-h-[200px] p-4 text-xs font-mono focus:outline-none border-0 resize-y bg-slate-900 text-slate-200 ${isCodeView ? 'block' : 'hidden'}`}
          style={{ minHeight: '200px' }}
          placeholder="HTML Kodunuzu buraya yazın..."
        />
      </div>
    </div>
  );
};

export default RichTextEditor;
